import {
    CountrySpecificConfig,
    DocumentType,
    LaunchDarklyProject,
    PushNotificationType,
} from '@superbet-group/web.lib.config';

import BonusProvider from '@core/models/bonus/BonusProvider';
import { Environment } from '@src/config/enums';
import { appLocale } from '@src/config/locale';
import { serviceProps } from '@src/config/playerAccountManagement/Betler';
import { HistoryType } from '@store/modules/ui/user/history/enums';
import { getIdApiUrls } from '@accountFederation/modules/idCardVerification/getid/constants';
import {
    WithdrawalType,
    DepositType,
} from '@core/models/user/enums';
import { Country } from '@superbet-group/web.types';

const config: CountrySpecificConfig = {
    country: Country.Rs,
    waitForRouteConfig: false,
    isBingoEnabled: false,
    checkSessionBeforeGameLaunch: true,
    services: {
        cashout: {
            isAvailable: true,
        },
    },
    header: {
        navbarItems: [
            'sport',
            'live',
            'games',
            'casinoLive',
            'myCasinoCasinoLive',
            'bingo',
            'virtual',
            'lottery',
            'results',
            'news',
            'promotions',
        ],
        items: [
            'sport',
            'live',
            'games',
            'casinoLive',
            'myCasinoCasinoLive',
            'bingo',
            'virtual',
            'lottery',
            'news',
            'results',
            'shops',
            'promotions',
        ],
    },
    auth: {
        usernameValidChars: '[A-Za-z0-9@$!%*#?&ăâîşţąćęłńóśźżČĆĐŠŽčćđšž]+',
        nameValidChars: '[A-Za-zÀ-žȘșȚț\\-,\\s\']+',
        portalDesktopId: 'Desktop_new',
        portalMobileId: 'Mobile_new',
        registerUrl: 'register',
        countryId: 184,
        emailMaxLength: 50,
        username: {
            requirements: {
                minLength: 6,
                maxLength: 20,
            },
        },
        password: {
            requirements: {
                minLength: 8,
                maxLength: 30,
                hasLowercase: true,
                hasUppercase: true,
                hasDigit: true,
            },
            isDistinctFromUsername: true,
            isDistinctFromEmail: true,
        },
        activateAccount: {
            buttonLaunchGameId: 52868168, // game externalId
            isResponsibleLimitButtonShown: false,
        },
    },
    user: {
        activateAccountRestRoute: 'activateWithLogin',
        idCardVerification: {
            period: 365, // in days. if user doesn't upload id card account will be disabled,
            isCompleteRegistrationNeededForKyc: true,
            maxRequiredDocumentImages: 1,
            isRemainingTimeVisible: false,
            preselectedCitizenship: 'Srbija',
        },
        withdrawal: {
            helpUrl: 'https://podrska.superbet.rs/hc/sr/sections/6882863712029',
            detailsOnlineWithdrawalUrl: 'https://support.superbet.com/hc/en-us/articles/4412310808977',
            isTaxable: false,
            isNewLabelVisible: {
                [WithdrawalType.Transaks]: true,
            },
            hasFees: true,
            isDepositRequired: false,
            idCardVerificationCheck: {
                isRequired: true,
                isRemainingTimeVisible: false,
            },
            hasBankTransferEndpoint: true,
            walletBreakdown: {
                isEnabled: true,
                isShown: true,
            },
        },
        deposit: {
            helpUrl: 'https://podrska.superbet.rs/hc/sr/sections/6882859581981',
            detailsOnlineDepositUrl: 'https://podrska.superbet.rs/hc/sr/articles/6883426219165',
            detailsBankTransferUrl: 'https://podrska.superbet.rs/hc/sr/articles/6883774511389',
            detailsPaySpot: '/wiki/metode-placanja#payspot',
            locationPaySpot: 'https://www.payspot.rs/lokator/',
            hasEligibility: true,
            idCardVerificationCheck: {
                isRequired: false,
                isRemainingTimeVisible: false,
            },
            isTaxable: false,
            isNewLabelVisible: {
                [DepositType.Transaks]: true,
            },
            hasLogosVisibleInProfile: true,
            bankTransfer: {
                iban: '160-0000000547071-06', // Account number
                currency: 'RSD', // Currency
                accountName: '4Bet, ACT 4 BET DOO Beograd', // Account name / Beneficiary:
                city: 'Tošin Bunar 274v, 11070 Beograd-Novi Beograd', // Address
                hasPaymentDescription: true,
            },
        },
        history: {
            availableTypes: {
                [HistoryType.transactions]: true,
                [HistoryType.bets]: true,
                [HistoryType.casinoPlays]: true,
            },
        },
        bonus: {
            faqUrlActive: '',
            faqUrlPending: 'https://sbt.bet/BonusuriInAsteptare',
            isWelcomeBonusShown: true,
            bonusProvider: BonusProvider.Betler,
            isLiveDealerBonusVisible: false,
            isWelcomeBonusIntroModalVisible: true,
            availableDetailFields: {
                bonusAmount: false,
                redeemedAmount: true,
                wageringRequirements: false,
                wageredAmount: false,
                amountLeftToWager: true,
                spinCasinoDetail: true,
                acceptanceDate: false,
                activationDate: true,
                termsAndConditionsUrl: false,
                expirationDate: true,
                amountGained: true,
            },
        },
        settings: {
            phoneDigitsNumber: 11,
            isPhoneWithPrefixEnabled: true,
            defaultCountryPrefix: '+381',
            privacyPolicyUrl: '/wiki/privacy-and-cookie-policy',
        },
        balance: {
            walletBreakdownHelpUrl: 'https://podrska.superbet.hr/hc/hr/articles/4403778053265',
        },
        responsibleGaming: {
            depositLimitUrl: 'https://podrska.superbet.rs/hc/sr/articles/6883957586717',
            realityCheckUrl: '',
            betLimitUrl: 'https://podrska.superbet.rs/hc/sr/articles/6884028104221',
            selfExclusionInfoUrl: 'https://podrska.superbet.rs/hc/sr/articles/6883999472157',
            responsibleGamingInfoUrl: '/wiki/odgovorno-kladjenje',
            accountClosureInfoUrl: 'https://podrska.superbet.rs/hc/sr/articles/6884052738205',
            timeOutInfoUrl: 'https://podrska.superbet.rs/hc/sr/articles/6884031708061',
            lossLimitUrl: 'https://podrska.superbet.rs/hc/sr/articles/6883990874781',
            sessionLimitUrl: 'https://podrska.superbet.rs/hc/sr/articles/6883992976669',
            isSelfExclusionModalPromotionsTextVisible: false,
            timeOut: {
                periods: [
                    {
                        name: '1day',
                        label: '1 day',
                        type: 'days',
                        value: 1,
                    },
                    {
                        name: '2days',
                        label: '2 days',
                        type: 'days',
                        value: 2,
                    },
                    {
                        name: '1week',
                        label: '1 week',
                        type: 'days',
                        value: 7,
                    },
                    {
                        name: '1month',
                        label: '1 month',
                        type: 'months',
                        value: 1,
                    },
                    {
                        name: '5months',
                        label: '5 months',
                        type: 'months',
                        value: 5,
                    },
                ],
                customMaxMonths: 5,
            },
            selfExclusionDefaultPeriod: 6,
        },
        isUpdateCustomParamsEnabled: false,
        isLicencePreferencesEnabled: false,
    },
    multiStepIdVerification: {
        documentSettings: {
            [DocumentType.Id]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: true,
                isSelfieRequired: false,
            },
            [DocumentType.Passport]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: false,
                isSelfieRequired: false,
            },
            [DocumentType.AdditionalDocument]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: false,
                isSelfieRequired: false,
            },
        },
    },
    sport: {
        isStreamAvailable: true,
        excludedStreamTournaments: [],
        v5Config: {
            language: appLocale.languageCode,
            applicationVariant: 'rssuperbetsport',
        },
        timeMarginForEventStart: 3,
        areTaxDetailsVisible: false,
    },
    utils: {
        bookieShopStartTime: 10,
        dateOfBirthFormat: 'dd/MM/yyyy',
    },
    footer: {
        whatsAppNumber: '',
        nonStopPhoneNumber: '',
        email: 'podrska@superbet.rs',
        faqPage: 'https://podrska.superbet.rs',
        offers: null,
        social: {
            twitter: '',
            facebook: '',
            youtube: '',
            instagram: '',
            tiktok: '',
        },
    },
    defaultLocation: {
        latitude: 44.439663,
        longitude: 26.096306,
    },
    isAutoLogoutEnabled: true,
    getSessionDuration: 2 * 60 * 1000, // 2 minutes
    isCasinoEnabled: true,
    showXJackpotOnPromoPage: false,
    news: {
        pageSize: 50,
        host: 'https://superbet-sportal-production.freetls.fastly.net/',
        project: 'superbet.hr',
    },
    corona: {
        areShopsDisabled: false,
    },
    supportEmail: 'podrska@superbet.rs',
    privacyEmail: 'data.protection@superbet.rs',
    virtualTicketsTranslations: {
        over: 'iznad',
        under: 'ispod',
    },
    betSlip: {
        allowSubmitWithMaxWin: true,
        showPayoutShoppingList: true,
        validationsConfig: {
            isEnabledWinLowerThanStake: true,
        },
    },
    marketFilters: {
        isEnabled: true,
    },
    maintenance: {
        defaultMaintenanceUrl: '/static/html/maintenance_rs.html',
    },
    multiStepTaxForSystemTickets: false,
    isPrintHistoryEnabled: false,
    launchDarkly: {
        clientIds: {
            [LaunchDarklyProject.DefaultProject]: '',
            [LaunchDarklyProject.ServiceStatus]: '',
        },
        userKeyVariable: serviceProps.launchDarklyUserKeyVariable,
    },
    social: {
        country: Country.Rs,
        env: 'stage',
    },
    pushNotificationType: PushNotificationType.ServerSentEvent,
    useSessionActivity: true,
    isLastSessionDisplayed: true,
    inbox: {
        availableDeepLinks: ['rssuperbetsport', 'rssuperbetgames'],
    },
};

const defaultProjectLDClientIds: Record<Environment, string> = {
    Dev: '62dfad735ba42811bd503c0b',
    Qa: '63284a62401d40117131832d',
    Stage: '631b31c6a0e557117a85e13f',
    Prod: '631b7b73a7a8d311cf319009',
};

const serviceStatusLDClientIds: Record<Environment, string> = {
    Dev: '631b32e160139c11a2ff513d',
    Qa: '631b32e160139c11a2ff513d', // same as stage to reduce number of ld envs
    Stage: '631b32e160139c11a2ff513d',
    Prod: '631b7c836ae30011bb4b444b',
};

switch (window.location.hostname) {
    case 'www.superbet.rs':
    case 'superbet.rs':
    case 'local.prod.superbet.rs':
    case 'stage.on.superbet.rs':
    case 'silent.superbet.rs':
    case 'www.silent.superbet.rs':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Prod,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Prod,
        };
        config.getIdApiUrl = getIdApiUrls.Prod;
        break;
    case 'www.stage.superbet.rs':
    case 'stage.superbet.rs':
    case 'local.stage.superbet.rs':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Stage,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Stage,
        };
        config.getIdApiUrl = getIdApiUrls.Stage;
        break;
    case 'www.qa.superbet.rs':
    case 'qa.superbet.rs':
    case 'local.qa.superbet.rs':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Qa,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Qa,
        };
        config.getIdApiUrl = getIdApiUrls.Qa;
        break;
    case 'www.dev.superbet.rs':
    case 'dev.superbet.rs':
    case 'local.superbet.rs':
    case 'local.dev.superbet.rs':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Dev,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Dev,
        };
        config.getIdApiUrl = getIdApiUrls.Dev;
        break;
    default:
        break;
}

export default config;
